import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import FieldError from '../components/ui/FieldError';
import { US_STATES } from '../constants/usStates';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../constants/countryCodes';

import Loading from '../components/ui/Loading';

const RepresentativeDetails = () => {
  const { user, updateUser } = useAuth();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Populate form when component mounts or user context updates
  useEffect(() => {
    if (user?.representativeDetails) {
      const details = user.representativeDetails;
      
      // Handle existing phone numbers that might include country codes
      let countryCode = details.countryCode || DEFAULT_COUNTRY_CODE;
      let phoneNumber = details.phone || '';
      
      // If phone number starts with a country code, extract it
      if (phoneNumber && phoneNumber.startsWith('+')) {
        const match = phoneNumber.match(/^(\+\d{1,4})\s?(.*)$/);
        if (match) {
          countryCode = match[1];
          phoneNumber = match[2];
        }
      }
      
      setFormData(prev => ({
        ...prev,
        ...details,
        countryCode,
        phone: phoneNumber
      }));
    }
  }, [user?.representativeDetails]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Ensure ZIP code only accepts numbers
    let processedValue = value;
    if (name === 'zipCode') {
      // Only allow numeric characters
      processedValue = value.replace(/[^0-9]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!formData.addressLine1.trim()) {
      setError('Street address is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return false;
    }
    if (!formData.zipCode || !formData.zipCode.toString().trim()) {
      setError('ZIP code is required');
      return false;
    }
    return true;
  };

  const handleContinue = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      // Prepare data for submission - combine country code and phone number
      const representativeDetailsToSave = {
        ...formData,
        phone: `${formData.countryCode} ${formData.phone}`.trim()
      };

      // Update user data with representative details
      updateUser({
        representativeDetails: representativeDetailsToSave
      });

      // Navigate to shop details page immediately
      navigate('/shop-details');

    } catch (err) {
      setError('Failed to save representative details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/business-type');
  };

  return (
    <ErrorBoundary>
      <div className="form-container">
        <div className="form-content">
          {/* Progress Indicator */}
          <div className="progress-indicator">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">Business Type</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step active">
              <div className="step-number">2</div>
              <div className="step-label">Representative</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">3</div>
              <div className="step-label">Shop Details</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">4</div>
              <div className="step-label">Review</div>
            </div>
          </div>

          <div className="form-header">
            <h1 className="form-title">Primary Representative Details</h1>
            <p className="form-subtitle">
              Please provide the details of the primary representative for your business
            </p>
          </div>

          <div className="form-body">
            <div className="form-sections">
              {/* Personal Information Section */}
              <div className="form-section">
                <h3 className="section-title">Personal Information</h3>
                <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter last name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone Number *</label>
                <div className="phone-input-container">
                  <select
                    id="countryCode"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    className="country-select"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={`${country.value}-${country.country}`} value={country.value}>
                        {country.country} {country.value}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input phone-input"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

                </div>
              </div>

              {/* Address Information Section */}
              <div className="form-section">
                <h3 className="section-title">Address Information</h3>
                <div className="form-grid">
                  {/* Street address */}
                  <div className="form-group full-width">
                    <label htmlFor="addressLine1" className="form-label">Street address *</label>
                    <input
                      type="text"
                      id="addressLine1"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Street address"
                    />
                  </div>

                  {/* Apartment / suite */}
                  <div className="form-group full-width">
                    <label htmlFor="addressLine2" className="form-label">Apartment, suite, building, etc. (optional)</label>
                    <input
                      type="text"
                      id="addressLine2"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Apartment, suite, building, etc."
                    />
                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label htmlFor="city" className="form-label">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="City"
                    />
                  </div>

                  {/* State */}
                  <div className="form-group">
                    <label htmlFor="state" className="form-label">State *</label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select a state</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ZIP code */}
                  <div className="form-group">
                    <label htmlFor="zipCode" className="form-label">ZIP code *</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="form-input zipcode-input"
                      placeholder="ZIP code"
                      maxLength="10"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>

                  {/* Country (disabled for now) */}
                  <div className="form-group">
                    <label htmlFor="country" className="form-label">Country</label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="United States">The United States of America</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleBack}
                className="btn btn-outline back-btn"
                disabled={isLoading}
              >
                ← Back
              </button>

              <FieldError error={error} />


              <button
                onClick={handleContinue}
                disabled={isLoading}
                className={`btn btn-primary continue-btn ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? <Loading /> : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default RepresentativeDetails;
